import { useEffect, useState, type JSX } from 'react';

export type ProvisioningStatus =
  | 'idle'
  | 'polling_first'
  | 'verifying_all'
  | 'configuring'
  | 'done'
  | 'failed_first_timeout'
  | 'failed_partial';

export interface ProvisioningOrder {
  id: string;
  status: string; // order.status (purchased / cancelled / …)
  provisioning_status: ProvisioningStatus;
  provisioning_polling_active: boolean;
  provisioning_started_at: string | null;
  provisioning_attempts: number;
  first_panel_ready_at: string | null;
  all_panels_ready_at: string | null;
  panel_count: number;
}

interface Props {
  order: ProvisioningOrder;
  panelsProvisionedCount: number;
  onAction: () => void | Promise<void>;
}

const STATUS_LABEL: Record<ProvisioningStatus, string> = {
  idle: 'Idle',
  polling_first: 'Polling first panel',
  verifying_all: 'Verifying all panels',
  configuring: 'Configuring',
  done: 'Done',
  failed_first_timeout: 'Failed: 24h timeout',
  failed_partial: 'Failed: stragglers (1h)',
};

/** Color variant matched to the existing ord-summary-stat-value-- palette. */
const STATUS_VARIANT: Record<ProvisioningStatus, 'default' | 'sage' | 'rose' | 'honey' | 'muted'> = {
  idle: 'muted',
  polling_first: 'honey',
  verifying_all: 'honey',
  configuring: 'default',
  done: 'sage',
  failed_first_timeout: 'rose',
  failed_partial: 'rose',
};

function formatElapsed(ms: number): string {
  if (ms < 0) return '0s';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin === 0 ? `${hr}h` : `${hr}h ${remMin}m`;
}

function relativeStarted(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function Stat({
  label,
  value,
  variant = 'default',
}: {
  label: string;
  value: number | string;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted';
}): JSX.Element {
  return (
    <div className="ord-summary-stat">
      <span className="ord-summary-stat-label">{label}</span>
      <span className={`ord-summary-stat-value ord-summary-stat-value--${variant}`}>{value}</span>
    </div>
  );
}

export function OrderProvisioningSection({ order, panelsProvisionedCount, onAction }: Props): JSX.Element {
  // Live ticker for in-flight stage timers; settled stages just freeze.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const startedAt = order.provisioning_started_at ? new Date(order.provisioning_started_at).getTime() : null;
  const firstReadyAt = order.first_panel_ready_at ? new Date(order.first_panel_ready_at).getTime() : null;
  const allReadyAt = order.all_panels_ready_at ? new Date(order.all_panels_ready_at).getTime() : null;

  const t1End = firstReadyAt ?? (order.provisioning_status === 'polling_first' && order.provisioning_polling_active ? now : null);
  const t1 = startedAt && t1End ? formatElapsed(t1End - startedAt) : '—';

  const t2Start = firstReadyAt;
  const t2End = allReadyAt ?? (order.provisioning_status === 'verifying_all' && order.provisioning_polling_active ? now : null);
  const t2 = t2Start && t2End ? formatElapsed(t2End - t2Start) : '—';

  const isActive = order.provisioning_polling_active;
  const isTerminal = ['done', 'failed_first_timeout', 'failed_partial'].includes(order.provisioning_status);
  const buttonLabel = isTerminal ? 'Restart polling' : isActive ? 'Stop polling' : 'Start polling';
  const buttonVariant = isActive ? 'secondary' : 'primary';
  const canAct = order.status === 'purchased';

  const pausedSuffix = !isActive && !isTerminal && order.provisioning_status !== 'idle' ? ' (paused)' : '';

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div className="ord-summary-top" style={{ marginBottom: 8 }}>
        <h3 className="ord-summary-title" style={{ fontSize: 16 }}>Provisioning verification</h3>
      </div>
      <div className="ord-summary-grid">
        <Stat
          label="Status"
          value={STATUS_LABEL[order.provisioning_status] + pausedSuffix}
          variant={STATUS_VARIANT[order.provisioning_status]}
        />
        <Stat
          label="Started"
          value={order.provisioning_started_at ? relativeStarted(order.provisioning_started_at) : '—'}
          variant="muted"
        />
        <Stat label="Attempts" value={order.provisioning_attempts} variant="default" />
        <Stat label="Time to first panel" value={t1} variant={firstReadyAt ? 'sage' : 'default'} />
        <Stat label="Time to verify all" value={t2} variant={allReadyAt ? 'sage' : 'default'} />
        <Stat
          label="Panels ready"
          value={`${panelsProvisionedCount}/${order.panel_count}`}
          variant={panelsProvisionedCount === order.panel_count && order.panel_count > 0 ? 'sage' : 'default'}
        />
      </div>
      <div className="ord-summary-actions">
        <button
          type="button"
          className={`ord-btn ord-btn--${buttonVariant}`}
          onClick={() => void onAction()}
          disabled={!canAct}
          title={
            isTerminal
              ? 'Reset state and start polling from scratch'
              : isActive
                ? 'Pause the 5-min cron tick (state preserved)'
                : 'Resume the 5-min cron tick (state preserved)'
          }
        >
          {buttonLabel}
        </button>
        {!canAct && (
          <span className="ord-summary-ok" style={{ color: 'var(--ink-muted)' }}>
            (only available on purchased orders)
          </span>
        )}
      </div>
    </div>
  );
}
